import React, { useState, useMemo, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import DeleteIcon from "@mui/icons-material/Delete";
import Alert from "@mui/material/Alert";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useReportTemplates } from "../../api/hooks/useReportTemplates";
import { useRequestReport } from "../../api/hooks/useReports";
import {
  ReportTemplateDto,
  TargetSelectionMode,
  CreateReportDto,
} from "../../api/types/dtos/report";
import { useOrganizationsListPaginated } from "../../api/hooks/useOrganizations";
import { useGroups } from "../../api/hooks/useGroups";
import { GroupDto } from "../../api/types/dtos/group";
import {
  OrganizationListDto,
  DateField,
  DateCondition,
  fetchOrganizationsListPaginated,
} from "../../api/services/organization.service";
import { useTablePagination, useSearchWithPagination } from "../../api/hooks/useTablePagination";

interface CreateReportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateReportModal: React.FC<CreateReportModalProps> = ({ open, onClose, onSuccess }) => {
  const { selectedOrganization } = useOrganization();

  const [step, setStep] = useState(1);
  const [targetMode, setTargetMode] = useState<TargetSelectionMode>("MANUAL");
  const [selectedOrgs, setSelectedOrgs] = useState<OrganizationListDto[]>([]);
  const [selectingAllMatching, setSelectingAllMatching] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  // Step 2 state
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplateDto | null>(null);
  const [reportName, setReportName] = useState("");
  const [generationDate, setGenerationDate] = useState("");

  // Date filter state (org selector)
  const [dateField, setDateField] = useState<DateField>("createdAt");
  const [dateCondition, setDateCondition] = useState<DateCondition>("after");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const { page, pageSize, handlePageChange, handlePageSizeChange, resetPagination } =
    useTablePagination({ initialPage: 0, initialPageSize: 25 });

  const { searchTerm, debouncedSearchTerm, setSearchTerm, clearSearch } = useSearchWithPagination(
    1000,
    resetPagination
  );

  const { data: orgListData, isLoading: orgListLoading } = useOrganizationsListPaginated(
    selectedOrganization?.id || "placeholder",
    {
      page,
      size: pageSize,
      searchTerm: debouncedSearchTerm || undefined,
      dateField: dateFrom ? dateField : undefined,
      dateCondition: dateFrom ? dateCondition : undefined,
      dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      dateTo: dateTo && dateCondition === "between" ? new Date(dateTo).toISOString() : undefined,
    },
    { enabled: !!selectedOrganization?.id && targetMode === "MANUAL" }
  );

  const { data: groupsData } = useGroups(
    selectedOrganization?.id || "placeholder",
    { isActive: true }
  );
  const availableGroups: GroupDto[] = useMemo(() => {
    return groupsData || [];
  }, [groupsData]);

  const { data: templates = [], isLoading: templatesLoading } = useReportTemplates();
  const requestReport = useRequestReport();

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setTargetMode("MANUAL");
      setSelectedOrgs([]);
      setSelectedGroupIds([]);
      setSelectedTemplate(null);
      setReportName("");
      setGenerationDate("");
      clearSearch();
      resetPagination();
      setDateField("createdAt");
      setDateCondition("after");
      setDateFrom("");
      setDateTo("");
    }
  }, [open, clearSearch, resetPagination]);

  // Auto-generate report name when template is selected
  useEffect(() => {
    if (selectedTemplate && !reportName) {
      setReportName(`${selectedTemplate.name} - ${new Date().toLocaleDateString("es")}`);
    }
  }, [selectedTemplate, reportName]);

  const currentPageOrgs = useMemo(() => {
    if (!orgListData?.data?.content) return [];
    return Array.isArray(orgListData.data.content) ? orgListData.data.content : [];
  }, [orgListData]);

  const allCurrentPageSelected = useMemo(() => {
    if (!currentPageOrgs.length) return false;
    return currentPageOrgs.every((org) => selectedOrgs.some((sel) => sel.id === org.id));
  }, [currentPageOrgs, selectedOrgs]);

  const someCurrentPageSelected = useMemo(() => {
    if (!currentPageOrgs.length) return false;
    return (
      currentPageOrgs.some((org) => selectedOrgs.some((sel) => sel.id === org.id)) &&
      !allCurrentPageSelected
    );
  }, [currentPageOrgs, selectedOrgs, allCurrentPageSelected]);

  const handleSelectAllCurrentPage = (checked: boolean) => {
    if (checked) {
      const newSelected = [...selectedOrgs];
      currentPageOrgs.forEach((org) => {
        if (!newSelected.some((sel) => sel.id === org.id)) {
          newSelected.push(org);
        }
      });
      setSelectedOrgs(newSelected);
    } else {
      setSelectedOrgs(
        selectedOrgs.filter((sel) => !currentPageOrgs.some((org) => org.id === sel.id))
      );
    }
  };

  const handleSelectAllMatchingFilter = useCallback(async () => {
    if (!selectedOrganization?.id) return;
    setSelectingAllMatching(true);
    try {
      const allOrgs: OrganizationListDto[] = [];
      let currentPage = 0;
      let hasMore = true;
      while (hasMore) {
        const response = await fetchOrganizationsListPaginated(selectedOrganization.id, {
          page: currentPage,
          size: 100,
          searchTerm: debouncedSearchTerm || undefined,
          dateField: dateFrom ? dateField : undefined,
          dateCondition: dateFrom ? dateCondition : undefined,
          dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
          dateTo:
            dateTo && dateCondition === "between" ? new Date(dateTo).toISOString() : undefined,
        });
        const content = response.data?.content;
        if (content && content.length > 0) {
          allOrgs.push(...content);
          currentPage++;
          if (content.length < 100 || currentPage >= (response.data.totalPages || 0)) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      setSelectedOrgs((prev) => {
        const newSelected = [...prev];
        allOrgs.forEach((org) => {
          if (!newSelected.some((sel) => sel.id === org.id)) {
            newSelected.push(org);
          }
        });
        return newSelected;
      });
    } catch (error) {
      console.error("Error selecting all matching organizations:", error);
    } finally {
      setSelectingAllMatching(false);
    }
  }, [selectedOrganization?.id, debouncedSearchTerm, dateField, dateCondition, dateFrom, dateTo]);

  const handleToggleOrg = (org: OrganizationListDto) => {
    setSelectedOrgs((prev) =>
      prev.some((sel) => sel.id === org.id)
        ? prev.filter((sel) => sel.id !== org.id)
        : [...prev, org]
    );
  };

  const handleRemoveOrg = (orgId: string) => {
    setSelectedOrgs((prev) => prev.filter((sel) => sel.id !== orgId));
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;

    const requestedAt = generationDate ? new Date(generationDate).toISOString() : undefined;

    const payload: CreateReportDto = {
      reportTemplateId: selectedTemplate.id,
      name: reportName || `Reporte ${new Date().toLocaleDateString("es")}`,
      targetSelectionMode: targetMode,
      requestedAt,
    };

    if (targetMode === "MANUAL") {
      payload.targetOrganizationIds = selectedOrgs.map((o) => o.id);
      if (payload.targetOrganizationIds.length === 0) return;
    } else if (targetMode === "BY_GROUPS") {
      payload.targetGroupIds = selectedGroupIds;
      if (!payload.targetGroupIds || payload.targetGroupIds.length === 0) return;
    }

    try {
      await requestReport.mutateAsync(payload);
      onSuccess();
    } catch {
      // Error handled by mutation state
    }
  };

  const handleClose = () => {
    onClose();
  };

  const canProceedFromStep1 = useMemo(() => {
    if (targetMode === "ALL_ORGS") return true;
    if (targetMode === "BY_GROUPS") return selectedGroupIds.length > 0;
    return selectedOrgs.length > 0;
  }, [targetMode, selectedGroupIds, selectedOrgs]);

  const selectionSummary = useMemo(() => {
    if (targetMode === "ALL_ORGS") return "Todas las organizaciones hijas";
    if (targetMode === "BY_GROUPS") return `${selectedGroupIds.length} grupo(s) seleccionado(s)`;
    return `${selectedOrgs.length} organizacion(es) seleccionada(s)`;
  }, [targetMode, selectedGroupIds, selectedOrgs]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {step === 1 ? "Paso 1: Selecciona Organizaciones" : "Paso 2: Configura el Reporte"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 400 }}>
        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: 500 }}>
            {/* Mode selector */}
            <ToggleButtonGroup
              value={targetMode}
              exclusive
              onChange={(_, value: TargetSelectionMode | null) => value && setTargetMode(value)}
              sx={{ alignSelf: "flex-start" }}
            >
              <ToggleButton value="MANUAL">Seleccion Manual</ToggleButton>
              <ToggleButton value="ALL_ORGS">Todas las Organizaciones</ToggleButton>
              <ToggleButton value="BY_GROUPS">Por Grupo</ToggleButton>
            </ToggleButtonGroup>

            {/* ALL_ORGS mode */}
            {targetMode === "ALL_ORGS" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 4 }}>
                <Alert severity="info">
                  Se generara el reporte para todas las organizaciones hijas activas de la
                  organizacion actual. La lista de organizaciones se resolvera en el servidor.
                </Alert>
              </Box>
            )}

            {/* BY_GROUPS mode */}
            {targetMode === "BY_GROUPS" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
                <Autocomplete
                  multiple
                  options={availableGroups}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={availableGroups.filter((g) => selectedGroupIds.includes(g.id || ""))}
                  onChange={(_, newValue) => {
                    setSelectedGroupIds(newValue.map((g) => g.id || "").filter(Boolean));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar Grupos"
                      placeholder="Buscar grupos..."
                      helperText={
                        !availableGroups.length
                          ? "No hay grupos disponibles. Crea grupos primero."
                          : `${selectedGroupIds.length} grupo(s) seleccionado(s)`
                      }
                    />
                  )}
                  noOptionsText="No hay grupos disponibles"
                />
              </Box>
            )}

            {/* MANUAL mode - existing org selection UI */}
            {targetMode === "MANUAL" && (
              <>
            {/* Search and selection controls */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                label="Buscar Organización"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                placeholder="Nombre, ID, grupo, dueño o teléfono"
                size="small"
              />
              {/* Date filter */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Campo de fecha</InputLabel>
                  <Select
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value as DateField)}
                    label="Campo de fecha"
                  >
                    <MenuItem value="createdAt">Fecha de creación</MenuItem>
                    <MenuItem value="updatedAt">Fecha de modificación</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Condición</InputLabel>
                  <Select
                    value={dateCondition}
                    onChange={(e) => setDateCondition(e.target.value as DateCondition)}
                    label="Condición"
                  >
                    <MenuItem value="after">Después de</MenuItem>
                    <MenuItem value="before">Antes de</MenuItem>
                    <MenuItem value="on">En</MenuItem>
                    <MenuItem value="between">Entre</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  type="datetime-local"
                  size="small"
                  label={dateCondition === "between" ? "Desde" : "Fecha"}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                />
                {dateCondition === "between" && (
                  <TextField
                    type="datetime-local"
                    size="small"
                    label="Hasta"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                )}
                {(dateFrom || dateTo) && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDateFrom("");
                      setDateTo("");
                    }}
                    title="Limpiar filtro de fecha"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allCurrentPageSelected}
                      indeterminate={someCurrentPageSelected}
                      onChange={(e) => handleSelectAllCurrentPage(e.target.checked)}
                      disabled={
                        orgListLoading || currentPageOrgs.length === 0 || selectingAllMatching
                      }
                    />
                  }
                  label="Seleccionar todos los de esta página"
                  sx={{ whiteSpace: "nowrap" }}
                />
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleSelectAllMatchingFilter()}
                  disabled={orgListLoading || selectingAllMatching}
                  startIcon={selectingAllMatching ? <CircularProgress size={16} /> : undefined}
                >
                  {selectingAllMatching
                    ? "Seleccionando..."
                    : "Seleccionar todos los de este filtro"}
                </Button>
              </Box>
            </Box>

            {/* Two-column layout */}
            <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 400 }}>
              {/* Left column: Available organizations */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Organizaciones Disponibles
                  </Typography>
                  {orgListLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexGrow: 1,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <List
                        sx={{
                          maxHeight: 350,
                          overflow: "auto",
                          border: "1px solid #eee",
                          borderRadius: 1,
                          flexGrow: 1,
                        }}
                        disablePadding
                      >
                        {currentPageOrgs.map((org) => {
                          const isSelected = selectedOrgs.some((sel) => sel.id === org.id);
                          return (
                            <ListItemButton
                              key={org.id}
                              onClick={() => handleToggleOrg(org)}
                              selected={isSelected}
                            >
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={`${org.name} (${org.idNumber})`}
                                secondary={`Dueño: ${org.owner?.name || "N/A"} | Tel: ${org.owner?.phones?.join(", ") || "N/A"} | Grupos: ${org.groupNames?.join(", ") || "N/A"}`}
                              />
                            </ListItemButton>
                          );
                        })}
                        {currentPageOrgs.length === 0 && !orgListLoading && (
                          <ListItem>
                            <ListItemText primary="No se encontraron organizaciones con los filtros actuales." />
                          </ListItem>
                        )}
                      </List>

                      {orgListData?.data && orgListData.data.totalPages > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 2,
                            pt: 2,
                            borderTop: "1px solid #eee",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Por página</InputLabel>
                              <Select
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(e.target.value as number)}
                                label="Por página"
                              >
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                              </Select>
                            </FormControl>
                            <Typography variant="body2" color="text.secondary">
                              Mostrando {orgListData.data.page * orgListData.data.size + 1}-
                              {Math.min(
                                (orgListData.data.page + 1) * orgListData.data.size,
                                orgListData.data.totalElements
                              )}{" "}
                              de {orgListData.data.totalElements} registros
                            </Typography>
                          </Box>
                          {orgListData.data.totalPages > 1 && (
                            <Pagination
                              count={orgListData.data.totalPages}
                              page={orgListData.data.page + 1}
                              onChange={(_event, value) => handlePageChange(value - 1)}
                              color="primary"
                              size="small"
                              showFirstButton
                              showLastButton
                            />
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Grid>

              {/* Right column: Selected organizations */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Organizaciones Seleccionadas ({selectedOrgs.length})
                  </Typography>
                  <List
                    sx={{
                      maxHeight: 350,
                      overflow: "auto",
                      border: "1px solid #eee",
                      borderRadius: 1,
                      flexGrow: 1,
                    }}
                    disablePadding
                  >
                    {selectedOrgs.map((org) => (
                      <ListItem
                        key={org.id}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveOrg(org.id)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={`${org.name} (${org.idNumber})`}
                          secondary={`Dueño: ${org.owner?.name || "N/A"} | Grupos: ${org.groupNames?.join(", ") || "N/A"}`}
                        />
                      </ListItem>
                    ))}
                    {selectedOrgs.length === 0 && (
                      <ListItem>
                        <ListItemText primary="No hay organizaciones seleccionadas." />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Grid>
            </Grid>
              </>
            )}
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
            <Alert severity="info">{selectionSummary}</Alert>

            <Autocomplete
              options={templates}
              getOptionLabel={(option) => `${option.name} (v${option.version})`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              loading={templatesLoading}
              value={selectedTemplate}
              onChange={(_, value) => setSelectedTemplate(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Plantilla de Reporte"
                  required
                  helperText={
                    !templates.length && !templatesLoading
                      ? "No hay plantillas disponibles. Crea una primero."
                      : ""
                  }
                />
              )}
              noOptionsText="No hay plantillas disponibles"
            />

            <TextField
              fullWidth
              label="Nombre del Reporte"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder={`Reporte ${new Date().toLocaleDateString("es")}`}
            />

            <TextField
              type="datetime-local"
              label="Fecha de generación (zona horaria local)"
              value={generationDate}
              onChange={(e) => setGenerationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Se convertirá automáticamente a UTC. Si no se especifica, se usará la fecha actual."
              sx={{ minWidth: 300 }}
            />

            {requestReport.isError && (
              <Alert severity="error">
                Error al solicitar el reporte. Verifica los datos e intenta nuevamente.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        {step === 1 && (
          <Button
            variant="contained"
            onClick={() => setStep(2)}
            disabled={!canProceedFromStep1 || (targetMode === "MANUAL" && (orgListLoading || selectingAllMatching))}
          >
            Siguiente
          </Button>
        )}
        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)}>Atrás</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={requestReport.isPending || !selectedTemplate || !canProceedFromStep1}
            >
              {requestReport.isPending ? <CircularProgress size={20} /> : "Solicitar Reporte"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateReportModal;
